import React, { useState, useEffect } from 'react';
import LearnerLayout from '@/components/LearnerLayout';
import { useSession } from '@/context/SessionContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Trophy, Star, Zap, Award, Rocket, Footprints, GraduationCap, PenTool,
  CheckCircle, MessageCircle, Lock, Loader2, TrendingUp, Target
} from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  'rocket': Rocket,
  'footprints': Footprints,
  'graduation-cap': GraduationCap,
  'star': Star,
  'trophy': Trophy,
  'pen-tool': PenTool,
  'check-circle': CheckCircle,
  'award': Award,
  'zap': Zap,
  'message-circle': MessageCircle,
};

const LearnerBadgesPage = () => {
  const { selectedSessionId } = useSession();
  const { t } = useLanguage();
  const [profile, setProfile] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API}/gamification/profile`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
      fetch(`${API}/gamification/leaderboard`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
    ]).then(([p, l]) => {
      if (p) setProfile(p);
      if (l) setLeaderboard(l);
    }).catch(console.error)
    .finally(() => setLoading(false));
  }, []);

  const triggerBadgeCheck = async () => {
    if (!selectedSessionId) return;
    const res = await fetch(`${API}/gamification/check-badges/${selectedSessionId}`, {
      method: 'POST', credentials: 'include'
    });
    if (res.ok) {
      // Refresh profile
      const p = await fetch(`${API}/gamification/profile`, { credentials: 'include' });
      if (p.ok) setProfile(await p.json());
    }
  };

  useEffect(() => {
    if (selectedSessionId) triggerBadgeCheck();
  }, [selectedSessionId]);

  if (loading) return (
    <LearnerLayout><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-teal-500" /></div></LearnerLayout>
  );

  const earnedIds = (profile?.badges || []).map(b => b.badge_id);
  const allBadges = profile?.badge_definitions || [];
  const xp = profile?.xp || 0;
  const level = profile?.level || 1;
  const progressPercent = profile?.xp_progress_percent || 0;

  return (
    <LearnerLayout>
      <div className="space-y-6" data-testid="learner-badges-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Trophy className="h-6 w-6 text-amber-500" />
              {t('gam_myBadges')}
            </h1>
            <p className="text-slate-500 mt-1">{t('gam_earnXP')}</p>
          </div>
        </div>

        {/* XP & Level Card */}
        <Card className="border-0 shadow-md overflow-hidden">
          <div className="bg-gradient-to-r from-[#0a3d62] to-[#38ada9] p-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-5">
                <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <span className="text-3xl font-black">{level}</span>
                </div>
                <div>
                  <p className="text-white/70 text-sm">{t('gam_level')}</p>
                  <p className="text-2xl font-bold">{t('gam_level')} {level}</p>
                  <p className="text-white/80 text-sm mt-1">{xp} {t('gam_xpTotal')}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-white/70 text-sm">{t('gam_badgesEarned')}</p>
                <p className="text-3xl font-bold">{earnedIds.length}<span className="text-lg text-white/60">/{allBadges.length}</span></p>
              </div>
            </div>
            <div className="mt-5">
              <div className="flex items-center justify-between text-sm text-white/80 mb-1">
                <span>{t('gam_progressToNext')} {level + 1}</span>
                <span>{progressPercent}%</span>
              </div>
              <div className="w-full h-3 bg-white/20 rounded-full overflow-hidden">
                <div className="h-full bg-white rounded-full transition-all duration-500" style={{ width: `${progressPercent}%` }} />
              </div>
            </div>
          </div>
        </Card>

        <Tabs defaultValue="badges">
          <TabsList className="grid grid-cols-2 w-full max-w-sm">
            <TabsTrigger value="badges"><Award className="h-4 w-4 mr-1" />{t('gam_badges')}</TabsTrigger>
            <TabsTrigger value="classement"><TrendingUp className="h-4 w-4 mr-1" />{t('gam_leaderboard')}</TabsTrigger>
          </TabsList>

          <TabsContent value="badges" className="mt-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {allBadges.map(badge => {
                const earned = earnedIds.includes(badge.badge_id);
                const earnedData = profile?.badges?.find(b => b.badge_id === badge.badge_id);
                const Icon = iconMap[badge.icon] || Award;
                return (
                  <Card key={badge.badge_id} className={`text-center transition-all ${
                    earned ? 'border-2 shadow-md' : 'opacity-50 grayscale'
                  }`} style={earned ? { borderColor: badge.color } : {}} data-testid={`badge-${badge.badge_id}`}>
                    <CardContent className="pt-6 pb-4">
                      <div className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-3 ${
                        earned ? '' : 'bg-slate-100'
                      }`} style={earned ? { backgroundColor: badge.color + '20' } : {}}>
                        {earned ? (
                          <Icon className="h-8 w-8" style={{ color: badge.color }} />
                        ) : (
                          <Lock className="h-6 w-6 text-slate-400" />
                        )}
                      </div>
                      <p className="font-semibold text-slate-900 text-sm">{badge.name}</p>
                      <p className="text-xs text-slate-500 mt-1">{badge.description}</p>
                      <div className="mt-2">
                        {earned ? (
                          <Badge className="text-xs" style={{ backgroundColor: badge.color + '20', color: badge.color }}>
                            +{badge.xp} XP
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs text-slate-400">
                            <Lock className="h-3 w-3 mr-1" />{badge.xp} XP
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="classement" className="mt-6">
            <Card>
              <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Trophy className="h-5 w-5 text-amber-500" />{t('gam_leaderboardTitle')}</CardTitle></CardHeader>
              <CardContent>
                {leaderboard.length === 0 ? (
                  <div className="text-center py-8">
                    <Trophy className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">{t('gam_noLeaderboard')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {leaderboard.map((entry) => {
                      const isMe = entry.user_id === profile?.user_id;
                      const medal = entry.rank === 1 ? 'text-amber-500' : entry.rank === 2 ? 'text-slate-400' : entry.rank === 3 ? 'text-amber-700' : 'text-slate-300';
                      return (
                        <div key={entry.user_id} className={`flex items-center gap-4 p-3 rounded-xl transition-colors ${
                          isMe ? 'bg-teal-50 border-2 border-teal-200' : 'bg-slate-50'
                        }`} data-testid={`rank-${entry.rank}`}>
                          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center font-bold shadow-sm">
                            {entry.rank <= 3 ? (
                              <Trophy className={`h-5 w-5 ${medal}`} />
                            ) : (
                              <span className="text-slate-600">{entry.rank}</span>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className={`font-semibold ${isMe ? 'text-teal-700' : 'text-slate-900'}`}>
                              {entry.name} {isMe && <span className="text-xs text-teal-500">{t('gam_you')}</span>}
                            </p>
                            <p className="text-xs text-slate-500">Niveau {entry.level} - {entry.badges_count} badges</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-slate-900">{entry.xp} <span className="text-xs text-slate-500">XP</span></p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </LearnerLayout>
  );
};

export default LearnerBadgesPage;
